import Block from '@mui/icons-material/Block';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useContext, useState } from 'react';

import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { useJob } from '~hooks/useJob/useJob';
import LoadsQueryContext from '~pages/Approvals/Files/components/JobsTable/ExpandedBlock/LoadsQueryContext';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import { makeLoadsJobFilters } from '~pages/Approvals/Files/loadsJobFilters';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import { isLoadAllRejected } from '../groupBillingLines';

export default function ChargesApproveAllLoadsButton() {
  const { userStore } = useStores();
  const { job, loads, refreshLoads, refreshParentJob } = useContext(LoadsQueryContext);
  const { chargesByJobId, reload } = useContext(ChargesBillingLinesQueryContext);
  const { filters } = useContext(FilesQueryContext);
  const { approveLoad } = useJob();
  const [isApproving, setIsApproving] = useState(false);
  const companyId = job.company?.id ?? getEffectiveUserCompanyId(userStore);
  const userPermissions = userStore.getPermissions();

  const loadsFilters = makeLoadsJobFilters(filters);
  const isLoadListNarrowed =
    loadsFilters.billingApproved !== undefined ||
    loadsFilters.ticketPotentialDuplicate !== undefined;
  const loadCharges = chargesByJobId.get(job.id)?.loads;
  const loadsToApprove = (loads ?? []).filter(
    (load) => !isLoadAllRejected(loadCharges?.get(load.id)),
  );
  const areAllLoadsRejected =
    !isLoadListNarrowed && (loads ?? []).length > 0 && loadsToApprove.length === 0;
  const loadApprovalStatuses = loadsToApprove.map((load) => ({
    load,
    ...getApprovalStatus(load, userPermissions, Boolean(job.order?.editable)),
  }));
  const approvableLoads = loadApprovalStatuses
    .filter(({ isApprovable }) => isApprovable)
    .map(({ load }) => load);
  const areAllLoadsApproved =
    !isLoadListNarrowed &&
    loadApprovalStatuses.length > 0 &&
    loadApprovalStatuses.every(({ isApproved }) => isApproved);

  const handleClick = async () => {
    setIsApproving(true);

    try {
      await Promise.allSettled(
        approvableLoads.map((load) => approveLoad(load.id, companyId)),
      );
    } finally {
      refreshLoads();
      refreshParentJob();
      reload();
      setIsApproving(false);
    }
  };

  if (areAllLoadsRejected) {
    return (
      <Box
        aria-disabled
        data-test-id="charges-all-loads-rejected"
        sx={(theme) => ({
          alignItems: 'center',
          color: theme.palette.text.disabled,
          display: 'inline-flex',
          gap: 0.5,
          height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
        })}
      >
        <Block sx={{ fontSize: '16px' }} />
        <Typography sx={{ color: 'inherit', fontSize: '12px', fontWeight: 600 }}>
          {t('approvals.approve_charges.all_rejected')}
        </Typography>
      </Box>
    );
  }

  if (areAllLoadsApproved) {
    return (
      <Box
        data-test-id="charges-all-loads-approved"
        sx={(theme) => ({
          alignItems: 'center',
          color: theme.brandV2.colors.treadGreen,
          display: 'inline-flex',
          gap: 0.5,
          height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
        })}
      >
        <CheckCircle sx={{ fontSize: '16px' }} />
        <Typography sx={{ color: 'inherit', fontSize: '12px', fontWeight: 600 }}>
          {t('approvals.approve_charges.all_approved')}
        </Typography>
      </Box>
    );
  }

  return (
    <SmallLoadingButton
      color="secondary"
      disabled={approvableLoads.length === 0}
      loading={isApproving}
      onClick={handleClick}
      variant="outlined"
    >
      {t('approvals.approve_charges.approve_all')}
    </SmallLoadingButton>
  );
}
